import { track } from "@amplitude/analytics-react-native";
import { useCallback, useEffect, useRef } from "react";
import Animated, {
	cancelAnimation,
	runOnJS,
	useAnimatedStyle,
	useSharedValue,
	withDelay,
	withSpring,
	withTiming,
} from "react-native-reanimated";
import preview_viewmaster from "src/assets/images/preview_viewmaster.png";
import RoundedButton from "src/components/RoundedButton";
import Touchable from "src/components/Touchable";
import useDim from "src/hooks/useDim";
import useKeyboardListener from "src/hooks/useKeyboardListener";
import useStyle from "src/hooks/useStyle";
import { color } from "src/styles";
import { FixedPosition } from "src/styles/position";
import { radiusMedium } from "src/styles/radius";
import { paddingLarge, paddingMedium, paddingSmall, paddingXXL } from "src/styles/spacing";
import { BlurView, Image, Text } from "src/swsh-native";

const GatheringPreviewPopUpDisplay = <T extends string>({
	popUpVariant,
	setPopUpVariant,
	setCurText,
	curText,
	joinLoading,
	handleJoinGathering,
}: {
	popUpVariant: T | null;
	setPopUpVariant: (newVariant: null) => void;
	setCurText: (newText: null) => void;
	curText: {
		title: string;
		description: string;
	} | null;
	joinLoading: boolean;
	handleJoinGathering: () => void;
}) => {
	const opacitySV = useSharedValue(0);
	const scaleSV = useSharedValue(0);
	const prevPopUpVariant = useRef<T | null>(null);

	const handleCloseAnimationComplete = useCallback(
		(curPopUpVariant: typeof popUpVariant) => {
			if (prevPopUpVariant.current !== curPopUpVariant) return;
			setCurText(null);
		},
		[setCurText],
	);

	useEffect(() => {
		cancelAnimation(scaleSV);
		cancelAnimation(opacitySV);

		if (popUpVariant) {
			scaleSV.value = withSpring(1, {
				damping: 20,
				stiffness: 300,
			});
			opacitySV.value = withTiming(1, { duration: 100 });
		} else {
			scaleSV.value = withDelay(200, withTiming(0, { duration: 100 }));
			opacitySV.value = withTiming(0, { duration: 200 }, () => {
				runOnJS(handleCloseAnimationComplete)(popUpVariant);
			});
		}
		prevPopUpVariant.current = popUpVariant;
	}, [handleCloseAnimationComplete, opacitySV, popUpVariant, scaleSV]);

	const animatedStyle = useAnimatedStyle(
		() => ({
			opacity: opacitySV.value,
			transform: [
				{
					scale: scaleSV.value,
				},
			],
		}),
		[opacitySV, scaleSV],
	);
	useKeyboardListener({
		Escape: () => {
			setPopUpVariant(null);
		},
	});
	const { theme } = useStyle();
	const dim = useDim({
		staticDim: true,
	});
	const width = Math.min(dim.width > 600 ? 360 : 320, dim.width - paddingLarge * 2);
	const minHeight = 200;

	if (!curText) return null;
	return (
		<>
			{
				// We want this to disappear instantly
				!!popUpVariant && (
					<Touchable
						animation="None"
						containerStyle={{
							position: FixedPosition,
							zIndex: 1,
							width: dim.width,
							height: dim.height,
						}}
						onPress={() => {
							setPopUpVariant(null);
						}}
					/>
				)
			}

			<Animated.View
				style={[
					animatedStyle,
					{
						position: FixedPosition,
						zIndex: 2,
						top: dim.height / 2 - minHeight,
						left: dim.width / 2 - width / 2,
						right: dim.width / 2 - width / 2,
						minHeight,
						borderRadius: radiusMedium,
						overflow: "hidden",
						borderWidth: 1,
						borderColor: color.ThinBorder[theme],
					},
				]}
			>
				<BlurView
					style={{
						width: "100%",
						minHeight,
						paddingVertical: paddingXXL,
						paddingHorizontal: paddingXXL,
					}}
				>
					<Image
						alt="View-Master"
						src={preview_viewmaster}
						dim={{
							width: 150,
							height: 150,
						}}
						style={{
							alignSelf: "center",
						}}
					/>
					<Text
						semibold
						semiLarge
						style={{
							marginTop: paddingMedium,
							marginLeft: paddingSmall,
							textAlign: "center",
						}}
					>
						{curText.title}
					</Text>
					<Text
						style={{
							textAlign: "center",
						}}
						small
					>
						{curText.description}
					</Text>
					<RoundedButton
						containerStyle={{
							marginTop: paddingLarge,
							marginHorizontal: paddingLarge,
						}}
						testID="GatheringPreviewPopUp-JoinButton"
						loading={joinLoading}
						colorVariant="Primary"
						sizeVariant="Flex"
						onPress={() => {
							track("GatheringPreview_popUpPress", {
								popUpVariant,
							});
							handleJoinGathering();
						}}
					>
						Join album
					</RoundedButton>
				</BlurView>
			</Animated.View>
		</>
	);
};
export default GatheringPreviewPopUpDisplay;
